import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useNavigation } from '@react-navigation/core'
import moment from 'moment'

import { addToJuniorWaitList, cancelBooking, getBookedSessions, getJuniorConsent, getJuniorWaitListStatus, getVolunteerProfile } from '../../../services/api/LearnDanish'
import { getConversationId } from '../../../services/api/Inbox'

import { CancelWithMessageDialog, CannotCancelDialog } from '../LearnDanish/dialogs/CancelDialogs'
import { SproglandJuniorConsentDialog } from '../../dialogs/SproglandJuniorConsentDialog'
import { VolunteerProfileDialog } from '../LearnDanish/dialogs/VolunteerProfileDialog'
import SproglandJuniorWaitlistDialog from '../LearnDanish/SproglandWaitListDialog'

import { txt } from '../../../locales/i18n'
import { CustomText } from '../../text/StyledText'
import { ButtonPrimary } from '../../buttons/StyledButtons'
import Colors from '../../../constants/Colors'
import JuniorCreateDialog from './JuniorCreateDialog'
import CustomPressable from '../../pressables/CustomPressable'
import NormalIcon from '../../icons/NormalIcon'
import { formatDate } from '../snakSammen/utils/helperFunctions'
import startVideoSession from '../../../utils/startVideoSession'
import Layout from '../../../constants/Layout'

export default function Junior({ junior, updateJunior, deleteJunior, }) {
	const navigation = useNavigation()

	const [sessions, setSessions] = useState([])
	const [onWaitlist, setOnWaitlist] = useState(false)

	const [waitlistModalVisible, setWaitlistModalVisible] = useState(false)
	const [juniorModalVisible, setJuniorModalVisible] = useState(false)
	const [consentModalVisible, setConsentModalVisible] = useState(false)
	const [cannotCancelModalVisible, setCannotCancelModalVisible] = useState(false)
	const [cancelMessageModalVisible, setCancelMessageModalVisible] = useState(false)

	const [volunteerProfile, setVolunteerProfile] = useState(null)
	const [volunteerProfileVisible, setVolunteerProfileVisible] = useState(false)

	const [loading, setLoading] = useState(false)

	moment.locale('da')

	useEffect(() => {
		getSessions()
	}, [])

	useEffect(() => {
		getJuniorWaitListStatus(junior.id).then(setOnWaitlist)
	}, [onWaitlist])

	const getSessions = async () => {
		const sessions = await getBookedSessions(junior.id)
		setSessions(sessions)
	}

	const handleWaitlistSignup = async (juniorId) => {
		const res = await addToJuniorWaitList(juniorId)
		if (res) setOnWaitlist(true)
		setWaitlistModalVisible(false)
	}

	async function handlePressVolunteer(volunteerId, volunteerName) {
		if (!volunteerId) return
		const volunteerProfile = await getVolunteerProfile(volunteerId)
		setVolunteerProfile({ description: volunteerProfile?.description, volunteerName })
		setVolunteerProfileVisible(true)
	}

	const handleStartSession = async (session) => {
		const consentGiven = await getJuniorConsent()
		if (consentGiven === false) return setConsentModalVisible(true)
		
		await startVideoSession(session.room_key, session.vonage_session_id, 'sproglandJunior')
	}

	const handleCancelSession = async (session) => {
		const now = new Date()
		const startDate = new Date(session.start_date)
		const TEN_MINUTES = 10 * 60 * 1000

		const tenMinutesBeforeStartDate = new Date(startDate.getTime() - TEN_MINUTES)

		if (now >= startDate && now >= tenMinutesBeforeStartDate) return setCannotCancelModalVisible(true)
		if (now < startDate && now > tenMinutesBeforeStartDate) return setCancelMessageModalVisible(true)
		
		await handleCancel(session)
	}

	const handleCancel = async (session) => {
		setLoading(true)
		await cancelBooking(session.id, true)
		getSessions()
		setCancelMessageModalVisible(false)
		setLoading(false)
	}

	const handleSendMessage = async (session) => {
		if (session.volunteer_id === undefined || session.id === undefined) return
		const conversationId = await getConversationId({ slSessionId: session.id, receiverId: session.volunteer_id })
		const params = { id: conversationId ?? 'new' }

		if (conversationId === null) {
			params.receiverId = session.volunteer_id
			params.slSessionId = session.id
		}

		const options = { screen: 'Conversation', initial: false, params }
		navigation.navigate('InboxStack', options)
	}

	async function handleSendMessageAndCancel(session) {
		await handleCancel(session)
		await handleSendMessage(session)
	}

	async function handleCancelMessage(session) {
		setCannotCancelModalVisible(false)
		await handleSendMessage(session)
	}

	return (
		<View style={styles.container}>
			<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 12 }}>
				<CustomText font="largeBold">{junior.name}</CustomText>

				<CustomPressable style={styles.closeIcon} onPress={() => setJuniorModalVisible(true)} accessibilityRole="button">
					<NormalIcon name="pen" size={18} color={Colors.inactive} />
				</CustomPressable>
			</View>

			{sessions.map((session) => {
				const date = formatDate(session.start_date, session.end_date)
					
				return (
					<View key={session.id} style={styles.sessionContainer}>
						<View style={{ flex: 2, justifyContent: 'center' }}>
							<CustomText font="bold">{date}</CustomText>
			
							<CustomPressable onPress={() => handlePressVolunteer(session.volunteer_id, session.username)}>
							<CustomText font="gotham-small" style={{textDecorationLine: 'underline'}}>
								<CustomText font="gotham-small">{txt('videoChat.volunteer.title') + ': '}</CustomText>
								{session.username}
							</CustomText>
							</CustomPressable>
						</View>

						<View style={styles.buttonsContainer}>
							<View style={styles.buttonStartContainer}>
								<ButtonPrimary
									title={txt('videoChat.start')}
									onPress={() => handleStartSession(session)}
									style={[styles.button, {backgroundColor: Colors.redCross.base}]}
									titleStyle={[styles.buttonTitle, {color: Colors.white}]}
								/>
							</View>
			
							<View style={styles.buttonStartContainer}>
								<ButtonPrimary
									title={Layout.small ? txt('videoChat.write') : txt('videoChat.messageSend')}
									onPress={() => handleSendMessage(session)}
									style={[styles.button, !Layout.small && { paddingHorizontal: 12 }]}
									titleStyle={styles.buttonTitle}
								/>
							</View>
			
							<View style={styles.buttonStartContainer}>
								<ButtonPrimary
									title={txt('videoChat.cancel')}
									onPress={() => handleCancelSession(session)}
									style={styles.button}
									titleStyle={styles.buttonTitle}
									loading={loading}
								/>
							</View>
						</View>

						<CancelWithMessageDialog
							visible={cancelMessageModalVisible}
							onDismiss={async () => await handleCancel(session)}
							sendMessage={() => handleSendMessageAndCancel(session)}
						/>

						<CannotCancelDialog
							visible={cannotCancelModalVisible}
							onDismiss={() => setCannotCancelModalVisible(false)}
							sendMessage={() => handleCancelMessage(session)}
						/>
					</View>
				)
			})}
			
			{!onWaitlist && !sessions.length && (
				<>
					<CustomText>{txt('learnDanish.junior.waitlist.description')}</CustomText>
					<ButtonPrimary 
						title={txt('learnDanish.junior.waitlist.add')}
						onPress={() => setWaitlistModalVisible(true)}
						style={styles.waitlistButton}
						titleStyle={{ color: Colors.white, fontSize: 14 }}
					/>
				</>
			)}

			{onWaitlist && !sessions.length && (
				<CustomText>{txt('learnDanish.junior.noSessions', { name: junior.name })}</CustomText>
			)}

			<SproglandJuniorWaitlistDialog
				visible={waitlistModalVisible}
				onDismiss={() => setWaitlistModalVisible(false)}
				submit={() => handleWaitlistSignup(junior.id)}
				junior={junior}
			/>

			<JuniorCreateDialog
				visible={juniorModalVisible}
				onDismiss={() => setJuniorModalVisible(false)}
				updateJunior={updateJunior}
				deleteJunior={deleteJunior}
				junior={junior}
			/>

			<SproglandJuniorConsentDialog
				visible={consentModalVisible}
				setVisible={(showConsentModal) => setConsentModalVisible(showConsentModal)}
				onCancel={() => setConsentModalVisible(false)}
				acceptOnly
			/>

			<VolunteerProfileDialog
				visible={volunteerProfileVisible}
				onDismiss={() => setVolunteerProfileVisible(false)}
				username={volunteerProfile?.volunteerName}
				description={volunteerProfile?.description}
			/>
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		marginHorizontal: 10,
		marginBottom: 24,
	},
	sessionContainer: {
		backgroundColor: Colors.white,
		borderRadius: 10,
		flexDirection: Layout.small ? 'column' : 'row',
		alignItems: Layout.small ? 'flex-start' : 'center',
		marginTop: 10,
		paddingVertical: Layout.small ? 18 : 12,
		paddingHorizontal: Layout.small ? 24 : 18,
	},
	buttonsContainer: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		marginTop: Layout.small ? 12 : 0,
		gap: 12,
	},
	buttonStartContainer: {
		flex: 1,
		justifyContent: 'center',
		paddingRight: 2,
	},
	button: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.background,
		paddingVertical: 13,
		paddingHorizontal: Layout.small ? 24 : 32,
		minWidth: Layout.small ? 75 : 115,
	},
	buttonTitle: {
		color: Colors.redCross.text,
		fontSize: 14,
	},
	waitlistButton: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.red,
		paddingVertical: 13,
		paddingHorizontal: 32,
		alignSelf: 'start',
		marginTop: 12,
	},
})